import React, { ReactNode, useState } from 'react';
import styled from 'styled-components';
import {
  DndContext,
  pointerWithin,
  useDroppable,
  useSensor,
  useSensors,
  MouseSensor,
  DragOverEvent,
  DragEndEvent,
  DragStartEvent,
  CollisionDetection,
} from '@dnd-kit/core';
import { SortableContext } from '@dnd-kit/sortable';

import { captureError, useDeepCompareMemo } from '@clatter/platform';
import { FolderManagerDragEndPayload, FolderManagerDragStartPayload } from '../../../interfaces';
import { ROOT_DROP_ID } from './FolderManagerFoldersView/FolderManagerFoldersView';
import { DragOverlay } from '../../UI';

// Custom collision: pointer-based, prefer specific folder targets over root background
const collisionWithRootFallback: CollisionDetection = (args) => {
  const hits = pointerWithin(args);
  const nonRoot = hits.filter((c) => c.id !== ROOT_DROP_ID);
  return nonRoot.length > 0 ? nonRoot : hits;
};

const StyledRootDropIndicator = styled.div<{ $isOver: boolean }>`
  position: absolute;
  inset: 0;
  pointer-events: none;
  border-radius: 6px;
  outline: ${({ $isOver, theme }) => ($isOver ? `2px solid ${theme.palette.primaryColor}` : '2px solid transparent')};
  outline-offset: -2px;
`;

// Droppable covering the full parent area (parent must be position:relative).
// DndContext/SortableContext produce no DOM nodes, so this div lands directly inside
// StyledGridContainer and uses absolute positioning to cover all the empty space.
// pointer-events:none so it doesn't block clicks; pointerWithin uses getBoundingClientRect math.
const RootDropZone = ({ children, overId }: { children: ReactNode; overId: string | null }) => {
  const { setNodeRef } = useDroppable({ id: ROOT_DROP_ID, data: { type: 'root' } });
  const isOver = overId === ROOT_DROP_ID;
  return (
    <>
      <StyledRootDropIndicator ref={setNodeRef} $isOver={isOver} />
      {React.Children.map(children, (child) =>
        // @ts-ignore
        // skip Fragment children — cloneElement would inject overId onto them, which React.Fragment doesn't accept
        React.isValidElement(child) && child.type !== React.Fragment ? React.cloneElement(child, { overId }) : child,
      )}
    </>
  );
};

//region INTERFACES
interface DraggableDnDContainerProps {
  children: ReactNode;
  items: FolderManagerItem[];
  onDragEnd: (data: FolderManagerDragEndPayload) => void;
  onDragStart?: (data: FolderManagerDragStartPayload) => void;
  disableInternalDnD?: boolean;
  enableRootDrop?: boolean;
  dragOverlayItems?: FolderManagerItem[];
}

//endregion

export const DraggableDnDContainer = ({ items, onDragEnd, onDragStart, children, disableInternalDnD = false, enableRootDrop = false, dragOverlayItems }: DraggableDnDContainerProps) => {
  const sensors = useSensors(
    useSensor(MouseSensor, {
      activationConstraint: {
        distance: 10, // activation constraint is needed to enable click events from slide item
      },
    }),
  );

  //region STATE
  const [overId, setOverId] = useState<string | null>(null);
  const [draggedItems, setDraggedItems] = useState<any[] | null>(null);
  //endregion

  //region COMPUTED
  const contextIds = useDeepCompareMemo(
    () =>
      items.map((item) => {
        if (!item.id) {
          captureError(new Error('Invalid data provided!'), { component: 'DraggableDnDContainer', skipConsole: true });
          return;
        }
        return item.id?.toString();
      }),
    [items],
  );
  //endregion

  //region METHODS
  const handleDragEnd = (data: DragEndEvent) => {
    setOverId(null);
    setDraggedItems(null);

    if (!data?.over?.id) return;

    if (enableRootDrop && data.over.id === ROOT_DROP_ID) {
      //@ts-ignore
      onDragEnd({ id: ROOT_DROP_ID, type: 'root' });
      return;
    }

    if (typeof onDragEnd === 'function') {
      //@ts-ignore
      onDragEnd({ id: data?.over?.id, type: data?.over?.data?.current?.['type'], item: data?.over?.data?.current?.['item'] });
    }
  };

  const handleDragStart = (event: DragStartEvent) => {
    if (!event || !event?.active?.id) {
      console.log('ERROR: missing dragging item id.');
    }

    const draggedItem = event?.active?.data?.current?.['item'];
    if (draggedItem) {
      setDraggedItems([draggedItem]);
    }

    if (!onDragStart || typeof onDragStart !== 'function') {
      console.log('No onDragStart method provided. Skipping.');
      return;
    }

    //@ts-ignore
    onDragStart({ id: event?.active?.id, item: draggedItem });
  };

  const handleDragOver = (event: DragOverEvent) => {
    const { active, over } = event;
    if (over?.id !== undefined && active?.id !== over?.id) {
      //@ts-ignore
      setOverId(over.id);
    } else if (!over) {
      setOverId(null);
    }
  };
  //endregion

  // when disableInternalDnD is true, just render children without DndContext
  // this allows using an external DndContext for cross-container drag and drop
  if (disableInternalDnD) {
    return (
      <SortableContext items={contextIds}>
        {React.Children.map(children, (child) =>
          // @ts-ignore
          // skip Fragment children — cloneElement would inject overId onto them, which React.Fragment doesn't accept
        React.isValidElement(child) && child.type !== React.Fragment ? React.cloneElement(child, { overId }) : child,
        )}
      </SortableContext>
    );
  }

  return (
    <DndContext
      sensors={sensors}
      collisionDetection={enableRootDrop ? collisionWithRootFallback : pointerWithin}
      onDragEnd={handleDragEnd}
      onDragStart={handleDragStart}
      onDragOver={handleDragOver}
    >
      {/*//@ts-ignore*/}
      <SortableContext items={contextIds}>
        {enableRootDrop ? (
          <RootDropZone overId={overId}>{children}</RootDropZone>
        ) : (
          React.Children.map(children, (child) =>
            // @ts-ignore
            // skip Fragment children — cloneElement would inject overId onto them, which React.Fragment doesn't accept
        React.isValidElement(child) && child.type !== React.Fragment ? React.cloneElement(child, { overId }) : child,
          )
        )}
      </SortableContext>

      <DragOverlay draggedItems={draggedItems ? (dragOverlayItems?.length ? dragOverlayItems : draggedItems) : null} />
    </DndContext>
  );
};
